import { useSatuEventTracker } from 'utils/satuEvent'
import { useAuth } from 'utils/auth'
import { isPathMatchRoute } from 'utils/router'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { REFERRER_KEY } from 'utils/hooks/useRouterHistory'

import type {
  TInspirationTrackerParams,
  TInspirationTrackerActivityType,
  TInspirationTrackerScreenNames,
} from 'types/inspirations'

const SATU_EVENT_NAME = 'guru_ide_praktik_v1'

const EVENT_TRIGGER_ROUTE_MAPPING: Record<
  string,
  TInspirationTrackerScreenNames
> = {
  '/home': 'HOMEPAGE',
  '/ide-praktik': 'IDE_PRAKTIK_EXPLORE_PAGE',
  '/ide-praktik/kategori': 'IDE_PRAKTIK_CATEGORY_LIST_PAGE',
  '/ide-praktik/kategori/:param': 'IDE_PRAKTIK_CATEGORY_PAGE',
  '/learning-group': 'KELAS',
  '/akun': 'AKUN',
}

export const useSETrackEvent = () => {
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()
  const { session } = useAuth()
  const [, , , getReferrerValue] = useLocalStorage(REFERRER_KEY, '')

  const isLoggedIn = !!session

  const SETrackCategoryClickIdePraktik = ({
    contentId,
    contentLocation,
    pageLocation,
  }: Pick<
    TInspirationTrackerParams,
    'contentId' | 'contentLocation' | 'pageLocation'
  >) => {
    const activityType: TInspirationTrackerActivityType = 'CLICK_IDE_PRAKTIK'
    const eventTrigger: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_location: [contentLocation, 'int'],
          content_id: contentId,
          page_location: pageLocation,
        }),
      },
    })
  }

  const SETrackExploreClickIdePraktik = ({
    contentId,
    contentLocation,
  }: Pick<TInspirationTrackerParams, 'contentId' | 'contentLocation'>) => {
    const activityType: TInspirationTrackerActivityType = 'CLICK_IDE_PRAKTIK'
    const eventTrigger: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_EXPLORE_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_EXPLORE_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_location: [contentLocation, 'int'],
          content_id: contentId,
        }),
      },
    })
  }

  const SETrackCategoryListOpenIdePraktik = ({
    categories,
  }: Pick<TInspirationTrackerParams, 'categories'>) => {
    const referrer = getReferrerValue()
    const eventTrigger = getEventTrigger(referrer)

    const activityType: TInspirationTrackerActivityType =
      'OPEN_IDE_PRAKTIK_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_LIST_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          categories,
        }),
      },
    })
  }

  const SETrackCategoryDetailOpenIdePraktik = ({
    displayRow,
    displayRowOrder,
    pageLocation,
  }: Pick<
    TInspirationTrackerParams,
    'pageLocation' | 'displayRow' | 'displayRowOrder'
  >) => {
    const referrer = getReferrerValue()
    const eventTrigger = getEventTrigger(referrer)

    const activityType: TInspirationTrackerActivityType =
      'OPEN_IDE_PRAKTIK_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          page_location: pageLocation,
          category_display_row_index: displayRow,
          category_display_column_order_index: displayRowOrder,
        }),
      },
    })
  }

  const SETrackExploreOpenIdePraktik = () => {
    const referrer = getReferrerValue()
    const eventTrigger = getEventTrigger(referrer)

    const activityType: TInspirationTrackerActivityType =
      'OPEN_IDE_PRAKTIK_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_EXPLORE_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
        }),
      },
    })
  }

  const SETrackBuktiKaryaExploreOpenIdePraktik = () => {
    const referrer = getReferrerValue()
    const eventTrigger = getEventTrigger(referrer)

    const activityType: TInspirationTrackerActivityType =
      'OPEN_IDE_PRAKTIK_PAGE'
    const screenName: TInspirationTrackerScreenNames = 'IDE_PRAKTIK_BK_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
        }),
      },
    })
  }

  const SETrackBuktiKaryaCategoryOpenIdePraktik = ({
    pageLocation,
  }: Pick<TInspirationTrackerParams, 'pageLocation'>) => {
    const referrer = getReferrerValue()
    const eventTrigger = getEventTrigger(referrer)

    const activityType: TInspirationTrackerActivityType =
      'OPEN_IDE_PRAKTIK_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_BK_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          page_location: pageLocation,
        }),
      },
    })
  }

  const SETrackBuktiKaryaExploreClickIdePraktik = ({
    contentId,
    contentPage,
  }: Pick<TInspirationTrackerParams, 'contentId' | 'contentPage'>) => {
    const activityType: TInspirationTrackerActivityType = 'CLICK_IDE_PRAKTIK'
    const eventTrigger: TInspirationTrackerScreenNames = 'IDE_PRAKTIK_BK_PAGE'
    const screenName: TInspirationTrackerScreenNames = 'IDE_PRAKTIK_BK_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_page: [contentPage, 'int'],
          content_id: contentId,
        }),
      },
    })
  }

  const SETrackBuktiKaryaCategoryClickIdePraktik = ({
    contentId,
    contentPage,
    pageLocation,
  }: Pick<
    TInspirationTrackerParams,
    'contentId' | 'contentPage' | 'pageLocation'
  >) => {
    const activityType: TInspirationTrackerActivityType = 'CLICK_IDE_PRAKTIK'
    const eventTrigger: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_BK_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_BK_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_page: [contentPage, 'int'],
          content_id: contentId,
          page_location: pageLocation,
        }),
      },
    })
  }

  const SETrackBuktiKaryaCategoryScrollIdePraktik = ({
    contentIds,
    contentPages,
    pageLocation,
  }: Pick<
    TInspirationTrackerParams,
    'contentIds' | 'contentPages' | 'pageLocation'
  >) => {
    const activityType: TInspirationTrackerActivityType = 'SCROLL_IDE_PRAKTIK'
    const eventTrigger: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_BK_PAGE'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_BK_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_id: contentIds.join(', '),
          content_page: contentPages.join(', '),
          page_location: pageLocation,
        }),
      },
    })
  }

  const SETrackBuktiKaryaExploreScrollIdePraktik = ({
    contentIds,
    contentPages,
  }: Pick<TInspirationTrackerParams, 'contentIds' | 'contentPages'>) => {
    const activityType: TInspirationTrackerActivityType = 'SCROLL_IDE_PRAKTIK'
    const eventTrigger: TInspirationTrackerScreenNames = 'IDE_PRAKTIK_BK_PAGE'
    const screenName: TInspirationTrackerScreenNames = 'IDE_PRAKTIK_BK_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_id: contentIds.join(', '),
          content_page: contentPages.join(', '),
        }),
      },
    })
  }

  const SETrackExploreScrollIdePraktik = ({
    contentIds,
    contentPages,
  }: Pick<TInspirationTrackerParams, 'contentIds' | 'contentPages'>) => {
    const eventTrigger = 'IDE_PRAKTIK_EXPLORE_PAGE'
    const activityType: TInspirationTrackerActivityType = 'SCROLL_IDE_PRAKTIK'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_EXPLORE_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_id: contentIds.join(', '),
          content_page: contentPages.join(', '),
        }),
      },
    })
  }

  const SETrackCategoryScrollIdePraktik = ({
    contentIds,
    contentPages,
    pageLocation,
  }: Pick<
    TInspirationTrackerParams,
    'contentIds' | 'contentPages' | 'pageLocation'
  >) => {
    const eventTrigger = 'IDE_PRAKTIK_CATEGORY_PAGE'
    const activityType: TInspirationTrackerActivityType = 'SCROLL_IDE_PRAKTIK'
    const screenName: TInspirationTrackerScreenNames =
      'IDE_PRAKTIK_CATEGORY_PAGE'

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_trigger: eventTrigger,
        screen_name: screenName,
        attributes: SETransformAttrs({
          is_login: [isLoggedIn, 'string'],
          content_id: contentIds.join(', '),
          content_page: contentPages.join(', '),
          page_location: pageLocation,
        }),
      },
    })
  }

  return {
    SETrackBuktiKaryaCategoryClickIdePraktik,
    SETrackBuktiKaryaCategoryOpenIdePraktik,
    SETrackBuktiKaryaCategoryScrollIdePraktik,
    SETrackBuktiKaryaExploreClickIdePraktik,
    SETrackBuktiKaryaExploreOpenIdePraktik,
    SETrackBuktiKaryaExploreScrollIdePraktik,
    SETrackCategoryClickIdePraktik,
    SETrackCategoryDetailOpenIdePraktik,
    SETrackCategoryListOpenIdePraktik,
    SETrackCategoryScrollIdePraktik,
    SETrackExploreClickIdePraktik,
    SETrackExploreOpenIdePraktik,
    SETrackExploreScrollIdePraktik,
  }
}

const getEventTrigger = (
  referrerPath: string
): TInspirationTrackerScreenNames => {
  for (const [route, eventTrigger] of Object.entries(
    EVENT_TRIGGER_ROUTE_MAPPING
  )) {
    if (isPathMatchRoute(referrerPath, route)) return eventTrigger
  }

  return 'EXTERNAL_LINK'
}
